import {
  AssessmentData,
  CourseData,
  FeedbackData,
  UpcomingClassData,
} from "@/interface/overview";
import { API_BASE_URL } from "@/utils/api";
import axios from "axios";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

export const useOverview = () => {
  const navigate = useNavigate();
  const [userName, setUserName] = useState("Student");
  const [greeting, setGreeting] = useState("Good Morning!");
  const [courses, setCourses] = useState<CourseData[]>([]);
  const [upcomingClass, setUpcomingClass] = useState<UpcomingClassData | null>(
    null,
  );
  const [assessments, setAssessments] = useState<AssessmentData[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAllCourses, setShowAllCourses] = useState(false);
  const [showAllAssessments, setShowAllAssessments] = useState(false);
  const [feedbacks, setFeedbacks] = useState<FeedbackData[]>([]);
  const [currentFeedbackIndex, setCurrentFeedbackIndex] = useState(0);

  useEffect(() => {
    const updateGreeting = () => {
      const hour = new Date().getHours();
      if (hour >= 5 && hour < 12) setGreeting("Good Morning!");
      else if (hour >= 12 && hour < 17) setGreeting("Good Afternoon!");
      else if (hour >= 17 && hour < 21) setGreeting("Good Evening!");
      else setGreeting("Good Night!");
    };
    updateGreeting();

    const fetchData = async () => {
      const storedUser = localStorage.getItem("user");
      const token = localStorage.getItem("token");

      if (!storedUser) return;

      const parsedUser = JSON.parse(storedUser);
      setUserName(parsedUser.name || parsedUser.email || "Student");

      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const apiDashboardUrl = `${API_BASE_URL}/dashboard/${parsedUser.id || parsedUser._id}`;

        const [coursesRes, upcomingRes, assessmentRes, feedbackRes] =
          await Promise.allSettled([
            axios.get(`${apiDashboardUrl}/my-classes`, { headers }),
            axios.get(`${apiDashboardUrl}/upcoming`, {
              headers,
            }),
            axios.get(
              `${API_BASE_URL}/quiz/history/${parsedUser.id || parsedUser._id}`,
              {
                headers,
              },
            ),
            axios.get(`${apiDashboardUrl}/feedback`, {
              headers,
            }),
          ]);

        if (coursesRes.status === "fulfilled") {
          setCourses(coursesRes.value.data.data || []);
        }

        if (upcomingRes.status === "fulfilled") {
          setUpcomingClass(upcomingRes.value.data.data);
        }

        if (assessmentRes.status === "fulfilled") {
          const sortedHistory = (assessmentRes.value.data || []).sort(
            (a: any, b: any) =>
              new Date(b.created_at).getTime() -
              new Date(a.created_at).getTime(),
          );
          setAssessments(sortedHistory);
        }

        if (feedbackRes.status === "fulfilled") {
          setFeedbacks(feedbackRes.value.data.data || []);
        }
      } catch (error) {
        console.error("Failed to fetch dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleNextFeedback = () => {
    setCurrentFeedbackIndex((prev) => (prev + 1) % feedbacks.length);
  };

  const handlePrevFeedback = () => {
    setCurrentFeedbackIndex(
      (prev) => (prev - 1 + feedbacks.length) % feedbacks.length,
    );
  };

  const currentFeedback =
    feedbacks.length > 0 ? feedbacks[currentFeedbackIndex] : null;

  // Logic slice data
  const displayedCourses = showAllCourses ? courses : courses.slice(0, 2);
  const displayedAssessments = showAllAssessments
    ? assessments
    : assessments.slice(0, 2);

  return {
    navigate,
    userName,
    greeting,
    courses,
    upcomingClass,
    assessments,
    loading,
    showAllCourses,
    showAllAssessments,
    feedbacks,
    currentFeedbackIndex,
    currentFeedback,
    displayedAssessments,
    displayedCourses,
    setShowAllCourses,
    setShowAllAssessments,
    handleNextFeedback,
    handlePrevFeedback,
  };
};
